import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { 
  IntakeFormTemplate, 
  FormSubmission, 
  canManageForms 
} from '../types/formBuilder';

// ===== FORM TEMPLATES CRUD =====

/**
 * Get all active forms
 */
export async function getAllForms(): Promise<IntakeFormTemplate[]> {
  try {
    const formsRef = collection(db, 'formTemplates');
    const q = query(formsRef, where('isActive', '==', true), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
      } as IntakeFormTemplate;
    });
  } catch (error) {
    console.error('Error fetching forms:', error);
    throw error;
  }
}

/**
 * Get forms by type
 */
export async function getFormsByType(type: string): Promise<IntakeFormTemplate[]> {
  try {
    const formsRef = collection(db, 'formTemplates');
    const q = query(
      formsRef, 
      where('type', '==', type),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
      } as IntakeFormTemplate;
    });
  } catch (error) {
    console.error('Error fetching forms by type:', error);
    throw error;
  }
}

/**
 * Get a single form by ID
 */
export async function getFormById(formId: string): Promise<IntakeFormTemplate | null> {
  try {
    const formRef = doc(db, 'formTemplates', formId);
    const formDoc = await getDoc(formRef);
    
    if (!formDoc.exists()) {
      return null;
    }
    
    const data = formDoc.data();
    return {
      id: formDoc.id,
      ...data,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
    } as IntakeFormTemplate;
  } catch (error) {
    console.error('Error fetching form:', error);
    throw error;
  }
}

/**
 * Helper function to remove undefined values from an object
 */
function removeUndefinedValues(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefinedValues(obj[key]);
      }
    }
    return cleaned;
  }
  
  return obj;
}

/**
 * Create a new form template
 */
export async function createForm(
  formData: Omit<IntakeFormTemplate, 'id' | 'createdAt' | 'updatedAt' | 'version'>,
  userEmail: string,
  userRole: string
): Promise<{ success: boolean; formId?: string; error?: string }> {
  try {
    // Check permissions
    if (!canManageForms(userRole)) {
      return { success: false, error: 'Insufficient permissions to create forms' };
    }

    const formsRef = collection(db, 'formTemplates');
    const formTemplate: Omit<IntakeFormTemplate, 'id'> = {
      ...formData,
      version: 1,
      createdBy: userEmail,
      createdAt: new Date().toISOString(),
      isActive: true
    };

    // Remove undefined values before saving to Firestore
    const cleanedData = removeUndefinedValues({
      ...formTemplate,
      createdAt: serverTimestamp(),
    });

    const docRef = await addDoc(formsRef, cleanedData);

    return { success: true, formId: docRef.id };
  } catch (error) {
    console.error('Error creating form:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to create form' 
    };
  }
}

/**
 * Update an existing form template
 */
export async function updateForm(
  formId: string,
  updates: Partial<IntakeFormTemplate>,
  userEmail: string,
  userRole: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Check permissions
    if (!canManageForms(userRole)) {
      return { success: false, error: 'Insufficient permissions to update forms' };
    }

    const formRef = doc(db, 'formTemplates', formId);
    const formDoc = await getDoc(formRef);
    
    if (!formDoc.exists()) {
      return { success: false, error: 'Form not found' };
    }

    const currentVersion = formDoc.data().version || 1;
    
    // Remove undefined values before updating Firestore
    const cleanedUpdates = removeUndefinedValues({
      ...updates,
      version: currentVersion + 1,
      updatedBy: userEmail,
      updatedAt: serverTimestamp()
    });
    
    await updateDoc(formRef, cleanedUpdates);

    return { success: true };
  } catch (error) {
    console.error('Error updating form:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to update form' 
    };
  }
}

/**
 * Soft delete a form (set isActive to false)
 */
export async function deleteForm(
  formId: string,
  userRole: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Check permissions
    if (!canManageForms(userRole)) {
      return { success: false, error: 'Insufficient permissions to delete forms' };
    }

    const formRef = doc(db, 'formTemplates', formId);
    const formDoc = await getDoc(formRef);
    
    if (!formDoc.exists()) {
      return { success: false, error: 'Form not found' };
    }

    await updateDoc(formRef, {
      isActive: false,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting form:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to delete form' 
    };
  }
}

/**
 * Duplicate a form template
 */
export async function duplicateForm(
  formId: string,
  newName: string,
  userEmail: string,
  userRole: string
): Promise<{ success: boolean; formId?: string; error?: string }> {
  try {
    // Check permissions
    if (!canManageForms(userRole)) {
      return { success: false, error: 'Insufficient permissions to duplicate forms' };
    }

    const formDoc = await getDoc(doc(db, 'formTemplates', formId));
    
    if (!formDoc.exists()) {
      return { success: false, error: 'Form not found' };
    }

    const originalData = formDoc.data();
    const newForm: Omit<IntakeFormTemplate, 'id'> = {
      ...originalData,
      name: newName,
      isDefault: false, // Duplicated forms are never default
      createdBy: userEmail,
      createdAt: new Date().toISOString(),
      version: 1
    } as Omit<IntakeFormTemplate, 'id'>;

    // Remove undefined values before saving to Firestore
    const cleanedData = removeUndefinedValues({
      ...newForm,
      createdAt: serverTimestamp()
    });

    const docRef = await addDoc(collection(db, 'formTemplates'), cleanedData);

    return { success: true, formId: docRef.id };
  } catch (error) {
    console.error('Error duplicating form:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to duplicate form' 
    };
  }
}

// ===== FORM SUBMISSIONS CRUD =====

/**
 * Save form submission
 */
export async function saveFormSubmission(
  formId: string,
  caseId: string,
  answers: { [fieldName: string]: any },
  userEmail: string,
  status: 'draft' | 'completed' = 'completed'
): Promise<{ success: boolean; submissionId?: string; error?: string }> {
  try {
    // Check if submission already exists for this form and case
    const submissionsRef = collection(db, 'formSubmissions');
    const q = query(
      submissionsRef,
      where('formId', '==', formId),
      where('caseId', '==', caseId)
    );
    const existingSubmissions = await getDocs(q);

    const form = await getFormById(formId);
    if (!form) {
      return { success: false, error: 'Form template not found' };
    }

    const submissionData = {
      formId,
      formVersion: form.version,
      caseId,
      answers,
      completedBy: userEmail,
      status,
      updatedAt: serverTimestamp()
    };

    let submissionId: string;

    if (!existingSubmissions.empty) {
      // Update existing submission
      const existingDoc = existingSubmissions.docs[0];
      submissionId = existingDoc.id;
      await updateDoc(doc(db, 'formSubmissions', submissionId), submissionData);
    } else {
      // Create new submission
      const docRef = await addDoc(submissionsRef, {
        ...submissionData,
        completedAt: serverTimestamp()
      });
      submissionId = docRef.id;
    }

    // Also save to the old location for backward compatibility
    // Save to patients/{caseId}/intakes/{formType} subcollection
    const intakeType = form.type === 'custom' ? form.customType || 'custom' : form.type;
    const intakeRef = doc(db, 'patients', caseId, 'intakes', intakeType);
    await updateDoc(intakeRef, {
      answers,
      completed: status === 'completed',
      completedAt: serverTimestamp(),
      completedBy: userEmail,
      formId,
      formVersion: form.version
    }).catch(async () => {
      // If document doesn't exist, create it
      const { setDoc } = await import('firebase/firestore');
      await setDoc(intakeRef, {
        answers,
        completed: status === 'completed',
        completedAt: serverTimestamp(),
        completedBy: userEmail,
        formId,
        formVersion: form.version
      });
    });

    return { success: true, submissionId };
  } catch (error) {
    console.error('Error saving form submission:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to save form submission' 
    };
  }
}

/**
 * Get form submission for a specific case
 */
export async function getFormSubmission(
  formId: string,
  caseId: string
): Promise<FormSubmission | null> {
  try {
    const submissionsRef = collection(db, 'formSubmissions');
    const q = query(
      submissionsRef,
      where('formId', '==', formId),
      where('caseId', '==', caseId)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const submissionDoc = snapshot.docs[0];
    const data = submissionDoc.data();
    
    return {
      id: submissionDoc.id,
      ...data,
      completedAt: data.completedAt instanceof Timestamp ? data.completedAt.toDate().toISOString() : data.completedAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
    } as FormSubmission;
  } catch (error) {
    console.error('Error fetching form submission:', error);
    throw error;
  }
}

/**
 * Get all submissions for a case
 */
export async function getCaseSubmissions(caseId: string): Promise<FormSubmission[]> {
  try {
    const submissionsRef = collection(db, 'formSubmissions');
    const q = query(submissionsRef, where('caseId', '==', caseId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        completedAt: data.completedAt instanceof Timestamp ? data.completedAt.toDate().toISOString() : data.completedAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
      } as FormSubmission;
    });
  } catch (error) {
    console.error('Error fetching case submissions:', error);
    throw error;
  }
}

/**
 * Check if a form has been completed for a case
 */
export async function isFormCompleted(formId: string, caseId: string): Promise<boolean> {
  try {
    const submission = await getFormSubmission(formId, caseId);
    return submission !== null && submission.status === 'completed';
  } catch (error) {
    console.error('Error checking form completion:', error);
    return false;
  }
}

